import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";
import { LATEST_ARTICLE, REACH_REPORT, SYNDICATION, WIRE_META } from "@/lib/wireData";

const STATS = [
  [WIRE_META.links, "Links"],
  [WIRE_META.stories, "Stories"],
  [REACH_REPORT.placements, "Outlets"],
  [REACH_REPORT.monthlyReach, "Monthly Reach"],
  [REACH_REPORT.countries, "Countries"],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function WireHighlight() {
  return (
    <section
      data-testid="wire-highlight-section"
      className="border-b hairline bg-white relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(0deg, #0F1117 1px, transparent 1px), linear-gradient(90deg, #0F1117 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-8 gap-x-6 mb-12 md:mb-16">
          <div className="col-span-12 lg:col-span-6">
            <div className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / The DCYB Wire — Press Index
            </div>
            <h2 className="mb-0">
              <img
                src="/dcyb-wire-logo.png?v=2"
                alt="DCYB Wire"
                data-testid="wire-highlight-logo"
                className="w-full max-w-[320px] sm:max-w-[400px] md:max-w-[480px] h-auto"
                draggable="false"
              />
              <span className="sr-only">The DCYB Wire</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex lg:items-end">
            <div>
              <p className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0F1117] leading-tight mb-4">
                Every Story. <span className="text-chrome">One Wire.</span>
              </p>
              <p
                data-testid="wire-highlight-subheading"
                className="text-base md:text-lg text-[#3D4049] leading-relaxed"
              >
                The next biggest news outlet for the next generation of digital
                infrastructure — national financial media, founder features, and
                the syndicated wire.
              </p>
            </div>
          </div>
        </div>

        {/* Stats — exposed skeleton */}
        <div
          data-testid="wire-highlight-stats"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-l border-t hairline mb-6 md:mb-8"
        >
          {STATS.map(([value, label]) => (
            <div
              key={label}
              className="border-r border-b hairline px-5 md:px-7 py-6 md:py-8 bg-gradient-to-b from-white to-[#F4F5F7] last:col-span-2 sm:last:col-span-1"
            >
              <div className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0F1117] leading-none mb-2">
                {value}
              </div>
              <div className="font-mono-label text-[9.5px] tracking-[0.18em] uppercase text-[#71747B]">
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Latest + syndication */}
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          <div className="col-span-12 lg:col-span-7">
            <Link
              to={LATEST_ARTICLE.url}
              data-testid="wire-highlight-latest"
              className="group chrome-surface relative block p-7 md:p-10 h-full transition-all duration-300 hover:shadow-[0_18px_44px_rgba(15,17,23,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1117]"
            >
              <CornerMarks color="#A9ADB4" />
              <div className="flex items-center gap-3 mb-5">
                <Newspaper size={13} className="text-[#0F1117]" aria-hidden />
                <span className="font-mono-label text-[10.5px] tracking-[0.16em] text-[#5F6168] uppercase">
                  Latest on the wire · {LATEST_ARTICLE.date} · {LATEST_ARTICLE.outlet}
                </span>
              </div>
              <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight leading-[1.1] text-[#0F1117] text-balance max-w-2xl mb-8">
                {LATEST_ARTICLE.headline}
              </h3>
              <span className="inline-flex items-center gap-2 font-mono-label text-[10.5px] tracking-[0.16em] uppercase text-[#5F6168] group-hover:text-[#0F1117] transition-colors">
                <span className="live-dot" aria-hidden />
                Read on The Wire
                <ArrowUpRight
                  size={13}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </Link>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#5F6168] mb-4">
              Syndicated across
            </div>
            <div className="grid grid-cols-3 gap-3">
              {SYNDICATION.outlets.map((o) => (
                <a
                  key={o.name}
                  href={o.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`wire-highlight-outlet-${slug(o.name)}`}
                  className="group flex flex-col items-center gap-2.5 bg-white px-2 py-4 border border-[#D2D5DA] transition-all duration-200 hover:border-[#9498A0] hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(15,17,23,0.08),inset_0_1px_0_rgba(255,255,255,0.95)]"
                  style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F6F7F9 100%)" }}
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white border border-[#DADCE1] shadow-[inset_0_1px_0_rgba(255,255,255,1),0_1px_1px_rgba(15,17,23,0.04)] overflow-hidden">
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${o.domain}&sz=128`}
                      alt=""
                      loading="lazy"
                      className="w-5 h-5 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </span>
                  <span className="font-mono-label text-[8px] tracking-[0.1em] uppercase text-[#5F6168] group-hover:text-[#0F1117] text-center leading-tight transition-colors">
                    {o.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 md:mt-12 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <Link
            to="/wire"
            data-testid="wire-highlight-cta"
            className="group chrome-btn-dark chrome-btn-dark-glow inline-flex items-center gap-2.5 font-mono-label text-[11px] tracking-[0.16em] px-7 py-4 self-start"
          >
            <span className="live-dot" aria-hidden />
            Open The Wire
            <ArrowUpRight
              size={15}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
          <span className="font-mono-label text-[9.5px] tracking-[0.18em] uppercase text-[#71747B]">
            Updated {WIRE_META.updated} · GlobeNewswire syndication report
          </span>
        </div>
      </div>
    </section>
  );
}
